/**
 * Seplat brand tokens.
 *
 * **Sourced, not invented.** Read out of Seplat Energy's own stylesheet —
 * `www.seplatenergy.com/assets/css/main.css`, retrieved 2026-08-13 via the Wayback Machine
 * (the live host returns HTTP 403 to every non-browser client), and cross-checked against
 * screenshots of the live site.
 *
 * Occurrence counts in that stylesheet, which is how the hierarchy below was established:
 *   #65B32E ×49   leaf green — the single most-used colour: rules, arrows, decorative shapes
 *   #006B42 ×42   mid green
 *   #005332 ×35   deep green — hero panels, "Regulatory news"-style headings
 *   #F9F9F9 ×19   page background
 *   #3C3C3B ×10   body text (a warm near-black, not a neutral grey)
 *   #F9B004 ×8    amber        ┐
 *   #EA5B1A ×8    orange       │ secondary/categorical set, used for sub-brands and
 *   #D25097 ×8    pink         │ data visualisation rather than core chrome
 *   #6C4796 ×8    purple       │
 *   #51BBB4 ×7    teal         │
 *   #143C89 ×7    navy         ┘
 *   #DE1915 ×6    Seplat red
 *
 * On the red: an earlier pass through the *Growth Academy* WordPress site had it down as a mere
 * button-hover colour. The screenshots of seplatenergy.com show otherwise — it is the "Seplat"
 * wordmark in the logo, the colour of section headings ("Our governance", "Our business"), and the
 * underline on call-to-action links. It is a primary brand colour and is treated as one here.
 *
 * Typography: the stylesheet loads **Aeonik** (licensed, so we cannot redistribute it) and
 * **Space Grotesk** (Google Fonts, and the face visible in the screenshots' display type). We use
 * Space Grotesk, self-hosted as WOFF2 — docs/01-PRODUCT-SPEC.md §4.1 requires self-hosting with no
 * external font CDN, for reliability on Nigerian mobile networks and for privacy. Aeonik is named
 * first in the stack so that a machine with it licensed and installed still gets the exact
 * corporate face.
 *
 * Corporate Communications still signs this off before go-live
 * (docs/06-DEPLOYMENT-AND-OPS.md go-live checklist). These are defensible, evidenced defaults.
 */

export const SEPLAT_BRAND = {
  /** Deep green — hero panels, primary headings. */
  deepGreen: "#005332",
  /** Mid green — gradients and hover states between the two greens. */
  midGreen: "#006B42",
  /** Leaf green — the signature accent: rules, arrows, decorative shapes. */
  leafGreen: "#65B32E",
  /** Seplat red — the wordmark, section headings, link underlines. */
  red: "#DE1915",
  /** Warm near-black used for body copy. */
  ink: "#3C3C3B",
  /** Page background. */
  offWhite: "#F9F9F9",
  white: "#FFFFFF",
  black: "#000000",
  border: "#CECECE",
} as const;

/** The secondary set, for sub-brands and future data visualisation. Not core chrome. */
export const SEPLAT_SECONDARY = {
  amber: "#F9B004",
  orange: "#EA5B1A",
  pink: "#D25097",
  purple: "#6C4796",
  teal: "#51BBB4",
  navy: "#143C89",
} as const;

/**
 * The colour tokens a `BrandProfile` row carries (docs/01-PRODUCT-SPEC.md §4.1). Every new form
 * inherits these.
 *
 * `primary` is the deep green rather than the red: it is what fills Seplat's own hero panels, and
 * a form is a calm document, not a poster. Red carries too much "error" weight in a form context
 * to be the default button colour — it stays available as `brandRed` for headings and accents.
 */
export const SEPLAT_BRAND_COLORS = {
  primary: SEPLAT_BRAND.deepGreen,
  primaryContrast: SEPLAT_BRAND.white,
  accent: SEPLAT_BRAND.leafGreen,
  brandRed: SEPLAT_BRAND.red,
  surface: SEPLAT_BRAND.white,
  background: SEPLAT_BRAND.offWhite,
  text: SEPLAT_BRAND.ink,
  border: SEPLAT_BRAND.border,
  error: SEPLAT_BRAND.red,
  success: SEPLAT_BRAND.leafGreen,
} as const;

export const SEPLAT_BRAND_FONTS = {
  heading: "Space Grotesk",
  body: "Space Grotesk",
  /** Full stack, mirroring seplatenergy.com's own `font-family` declarations. */
  stack: '"Aeonik", "Space Grotesk", Helvetica, Arial, sans-serif',
} as const;

export type BrandColors = typeof SEPLAT_BRAND_COLORS;
